import useIdToFindResourceList from '@/views/creative/utils/use-id-to-find-resource-list'
import { Message } from 'element-ui'
import i18n from '@/lang'
import generateId from '@/utils/generateId'
import updateResourceList from '@/views/creative/utils/update-resource-list'
import _ from 'lodash'
import {
  defaultEnableFillColor,
  defaultImageFilters,
} from '@/store/creative/common/resource'
import { isVideoResourceAndHasAudio } from '@com/utils/file'
import useIdToFindCategory from '@/views/creative/utils/use-id-to-find-category'
import { getAnimationList } from '@/views/creative/utils/model-animation-utils'
import { getAllSkinTexturePrefixByCategory } from '@/views/creative/utils/model-material-utils'
import usePrefixToFindCategoryId from '@/views/creative/utils/use-prefix-to-find-category-id'

// 对资源的编辑（新增、删除、重置、设置默认资源）时，需要更新联动更新
function updateRefCategoryResources({ list, state }) {
  const basicResource = list.find((item) => item.is_basic === 1)

  if (basicResource && basicResource.category_ids) {
    const refCategory = basicResource.category_ids.split(',')
    // 联动更新
    if (refCategory.length > 1) {
      refCategory.forEach((refId) => {
        refId &&
          updateResourceList({
            resource: state.templateConfig.resource,
            categoryId: Number(refId),
            list,
          })
      })
    }
  }
}

export async function addResource({ commit, state, dispatch }, resource) {
  const list = useIdToFindResourceList({
    resource: state.templateConfig.resource,
    id: resource.category_id,
  })
  const basicResource = list.find((item) => item.is_basic === 1)

  if (
    list
      .filter((item) => item.action !== 'del')
      .some((item) => item.file_md5 === resource.file_md5)
  ) {
    Message({
      message: i18n.t('playTurbo.custom.resourceDialog.uploadWarning'),
      type: 'error',
    })
  } else {
    let extra = resource.extra || {}
    // 视频资源并且存在关联音频
    if (isVideoResourceAndHasAudio(basicResource)) {
      extra = { ...basicResource.extra }
    }
    const newResource = {
      ...resource,
      action: 'create',
      id: 'fe_id:' + generateId(),
      category_id: resource.category_id,
      category_ids: basicResource.category_ids, // 关联分类 id
      extra: {
        ...extra,
        origin_file_url: extra.origin_file_url || resource.file_url,
        config: {
          ...extra.config,
          enableFillColor: defaultEnableFillColor,
          filters: {
            ...defaultImageFilters,
          },
        },
      },
    }
    list.push(newResource)
    updateResourceList({
      resource: state.templateConfig.resource,
      categoryId: resource.category_id,
      list,
    })
    const tempResource = {
      ...newResource,
      is_default: 1,
    }
    commit('updateCurrentResource', tempResource)
    await dispatch('changeDefaultResource', tempResource)

    updateRefCategoryResources({ list, state })
  }
}

export async function removeResource({ state, dispatch, commit }, resource) {
  const list = useIdToFindResourceList({
    resource: state.templateConfig.resource,
    id: resource.category_id,
  })
  const index = list.findIndex((item) => item.id === resource.id)

  // 如果是新增的资源直接删除即可
  if (resource.action === 'create') {
    list.splice(index, 1)
  } else {
    // 如果是原有数据，需要将加 action 属性，值为 del
    list.splice(index, 1, { ...list[index], action: 'del' })
  }

  updateResourceList({
    resource: state.templateConfig.resource,
    categoryId: resource.category_id,
    list,
  })

  updateRefCategoryResources({ list, state })
}

/**
 * 更新资源，可更新的包括标签 id、名字、url等
 * @param {any} state
 * @param {any} resource
 */
export async function updateResource({ state, dispatch, commit }, resource) {
  const list = useIdToFindResourceList({
    resource: state.templateConfig.resource,
    id: resource.category_id,
  })
  const index = list.findIndex((item) => item.id === resource.id)
  const targetResource = list[index]

  if (targetResource) {
    const newResource = {
      ...targetResource,
      ...resource,
      // 更新时，如果有 action 的话，原有的 action 优先级更高，因为原有的 action 可能为 add 或 update
      action:
        targetResource.action === 'create' ? targetResource.action : 'update',
    }
    list.splice(index, 1, newResource)
    updateResourceList({
      resource: state.templateConfig.resource,
      categoryId: resource.category_id,
      list,
    })
    updateRefCategoryResources({ list, state })
    commit('updateCurrentResource', newResource)

    dispatch('livePreviewResources')
  } else {
    throw new Error(i18n.t('playTurbo.creative.store.error'))
  }
}

export async function changeDefaultResource({ state, dispatch }, resource) {
  const list = useIdToFindResourceList({
    resource: state.templateConfig.resource,
    id: resource.category_id,
  })

  list.forEach((item, index) => {
    // 将原本默认的改为非默认
    if (item.is_default === 1) {
      list.splice(index, 1, {
        ...item,
        is_default: 0,
        action: item.action === 'create' ? item.action : 'update',
      })
    }
  })

  const index = list.findIndex((item) => item.id === resource.id)
  const targetResource = list[index]

  if (targetResource) {
    list.splice(index, 1, {
      ...targetResource,
      ...resource,
      is_default: 1,
      action:
        targetResource.action === 'create' ? targetResource.action : 'update',
    })
    updateResourceList({
      resource: state.templateConfig.resource,
      categoryId: resource.category_id,
      list,
    })

    updateRefCategoryResources({ list, state })

    dispatch('livePreviewResources')
  } else {
    throw new Error(i18n.t('playTurbo.creative.store.error'))
  }
}

export async function resetResource(
  { state, dispatch, commit },
  { categoryId },
) {
  const resetBeforeExecution = (id) => {
    const list = useIdToFindResourceList({
      resource: state.templateConfig.resource,
      id: id,
    })
    list.push({
      action: 'reset',
      category_id: id,
    })
    state.templateConfig.resource[id] = list
    // reset 引用资源坑位
    updateRefCategoryResources({ list, state })
  }
  resetBeforeExecution(categoryId)
  const category = useIdToFindCategory({
    category: state.templateConfig.category,
    categoryId: categoryId,
  })
  const animationList = getAnimationList(category)
  if (animationList.length) {
    animationList.forEach((item) => {
      resetBeforeExecution(item.category_id)
    })
  }
  const allSkinTexturePrefix = getAllSkinTexturePrefixByCategory(category)
  if (allSkinTexturePrefix.length) {
    allSkinTexturePrefix.forEach((item) => {
      resetBeforeExecution(
        usePrefixToFindCategoryId({
          resource: state.templateConfig.resource,
          prefix: item,
        }),
      )
    })
  }
  dispatch(
    'creative/modelEditor/resetModelModifyConfigByCategoryId',
    {
      categoryId,
    },
    { root: true },
  )
  // reset 之后先保存一次，告知服务端重置了资源坑位
  await dispatch('saveTemplate', {
    omitKeys: ['category_config'],
  })

  const resetAfterExecution = (id) => {
    const resources = (_.get(state.originalResources, `${id}`) || []).map(
      (item) => {
        return {
          ...item,
          file_path_for_live_preview: item.file_path,
          preview_urls_for_live_preview: _.cloneDeep(item.preview_urls),
        }
      },
    )
    state.templateConfig.resource[id] = resources
    updateRefCategoryResources({ list: resources, state })
  }

  resetAfterExecution(categoryId)
  if (animationList.length) {
    animationList.forEach((item) => {
      resetAfterExecution(item.category_id)
    })
  }
  if (allSkinTexturePrefix.length) {
    allSkinTexturePrefix.forEach((item) => {
      resetAfterExecution(
        usePrefixToFindCategoryId({
          resource: state.templateConfig.resource,
          prefix: item,
        }),
      )
    })
  }

  commit('updatePreviewGroupConfig', _.get(state, 'groupsConfig[0].name'))

  dispatch('livePreviewResources')
}
